// The Studio app's screens on the site: the /studio showcase (#studio-app). Real, raw app screens (the store
// renders' caption bands and device art cropped off; the site draws its own Mac and iPhone), as WebP in
// src/assets/studio-app, with the fictional "Northline Race Photo" workspace and its demo orders and buyers.
//
// SOURCE: the Studio app's App Store renders (photohawk-uploader test/store/store_screenshots_test.dart, en-US), in the
// app's LIGHT theme (its default; owner, 27 Oct 2026), rendered 27 Oct 2026 from photohawk-uploader 912bb4a
// (branch claude/photohawk-multigallery-previews-acq18r):
//   mac-NN-<id>.png (2880×1800): the window's content below its title bar (x 360, y 387), 2160×1341, as 1080 / 2160 w;
//   iphone69-NN-<id>.png (1320×2868): the screen inside the phone, with its status bar (x 191, y 719), 938×2049, as
//   320 / 640 w.
// The race photos in the gallery and upload screens are the app's demo photos: public domain (credits in
// src/assets/studio-app/CREDITS.md). To swap: crop the new renders the same way, replace the files below, and keep
// `width`/`height` the crop's size.

import macLive1080 from "@/assets/studio-app/mac-live-capture-1080.webp";
import macLive2160 from "@/assets/studio-app/mac-live-capture-2160.webp";
import macUpload1080 from "@/assets/studio-app/mac-upload-1080.webp";
import macUpload2160 from "@/assets/studio-app/mac-upload-2160.webp";
import macToday1080 from "@/assets/studio-app/mac-today-1080.webp";
import macToday2160 from "@/assets/studio-app/mac-today-2160.webp";
import macGalleries1080 from "@/assets/studio-app/mac-galleries-1080.webp";
import macGalleries2160 from "@/assets/studio-app/mac-galleries-2160.webp";
import macOrders1080 from "@/assets/studio-app/mac-orders-1080.webp";
import macOrders2160 from "@/assets/studio-app/mac-orders-2160.webp";
import phoneToday320 from "@/assets/studio-app/iphone-today-320.webp";
import phoneToday640 from "@/assets/studio-app/iphone-today-640.webp";
import phoneUpload320 from "@/assets/studio-app/iphone-upload-320.webp";
import phoneUpload640 from "@/assets/studio-app/iphone-upload-640.webp";
import phoneGalleries320 from "@/assets/studio-app/iphone-galleries-320.webp";
import phoneGalleries640 from "@/assets/studio-app/iphone-galleries-640.webp";
import phoneOrders320 from "@/assets/studio-app/iphone-orders-320.webp";
import phoneOrders640 from "@/assets/studio-app/iphone-orders-640.webp";

export interface StudioShot {
  src: string;
  srcSet: string;
  width: number;
  height: number;
}

const mac = (a: string, b: string): StudioShot => ({ src: a, srcSet: `${a} 1080w, ${b} 2160w`, width: 2160, height: 1341 });
const phone = (a: string, b: string): StudioShot => ({ src: a, srcSet: `${a} 320w, ${b} 640w`, width: 938, height: 2049 });

export const STUDIO_APP_SHOTS = {
  /** The Mac window: Live capture, four cameras (Canon, Nikon, Sony, Fujifilm) sending to Riverside 10K › Finish. */
  mac: mac(macLive1080, macLive2160),
  /** The iPhone: Today (sales, orders, photos delivered, galleries). */
  phone: phone(phoneToday320, phoneToday640),
} satisfies Record<string, StudioShot>;

/** The "Inside the Studio app" screens (#studio-app-screens): four Mac windows and three iPhone screens. */
export const STUDIO_APP_SCREENS = {
  /** Upload: local photos picked on the left, the gallery's collections on the right, the queue below (8 of 9). */
  macUpload: mac(macUpload1080, macUpload2160),
  /** Today: sales, average order, photos delivered, storage, recent orders and what needs attention. */
  macToday: mac(macToday1080, macToday2160),
  /** A gallery's Photos & videos tab: 2,214 photos, bib or text search, collection chips. */
  macGalleries: mac(macGalleries1080, macGalleries2160),
  /** An order with the Refund this order dialog: full or partial, a reason, then the confirm bar. */
  macOrders: mac(macOrders1080, macOrders2160),
  /** The iPhone's transfer queue: uploading 8 of 9, pause, cancel, up to 64 at once. */
  phoneUpload: phone(phoneUpload320, phoneUpload640),
  /** The iPhone's galleries: drafts and published, with net sales. */
  phoneGalleries: phone(phoneGalleries320, phoneGalleries640),
  /** The iPhone's orders: paid, awaiting shipment, refunded. */
  phoneOrders: phone(phoneOrders320, phoneOrders640),
} satisfies Record<string, StudioShot>;
